import React from 'react';
import { createPortal } from 'react-dom';
import {
  mergeSlideColorSuggestions,
  normalizeSlidePickerHex,
} from '../../utils/slideColorHistory';
import {
  clampByte,
  hexToSlideHsv,
  hsvToSlideHex,
  hueToCssHsl,
  parseSlideRgbHex,
  type SlideHsv,
} from '../../utils/slideColorPickerModel';
import {
  computeZoomAwareAnchorCenteredPopupLayout,
  getLayoutViewportSize,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';

export type SlideColorPickerFieldProps = {
  value: string;
  fallback?: string;
  ariaLabel: string;
  recentColors: string[];
  usedColors: string[];
  disabled?: boolean;
  surface?: 'dark' | 'light';
  swatchStyle?: React.CSSProperties;
  onOpen?: () => void;
  onPreview: (hex: string) => void;
  onConfirm: (hex: string) => void;
};

type ChannelFormat = 'rgb' | 'hex';

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

function getEyeDropperCtor(): EyeDropperCtor | null {
  if (typeof window === 'undefined') return null;
  return (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper ?? null;
}

function pointerRatio(el: HTMLElement, clientX: number, clientY: number): { x: number; y: number } {
  const r = el.getBoundingClientRect();
  return {
    x: r.width <= 0 ? 0 : Math.min(1, Math.max(0, (clientX - r.left) / r.width)),
    y: r.height <= 0 ? 0 : Math.min(1, Math.max(0, (clientY - r.top) / r.height)),
  };
}

export function SlideColorPickerField({
  value,
  fallback = '#141414',
  ariaLabel,
  recentColors,
  usedColors,
  disabled = false,
  surface = 'dark',
  swatchStyle,
  onOpen,
  onPreview,
  onConfirm,
}: SlideColorPickerFieldProps) {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties | null>(null);
  const [format, setFormat] = React.useState<ChannelFormat>('rgb');
  const dirtyRef = React.useRef(false);
  const latestHexRef = React.useRef(normalizeSlidePickerHex(value, fallback) || fallback);
  const hsvRef = React.useRef<SlideHsv>({ h: 0, s: 0, v: 0 });

  const hex = normalizeSlidePickerHex(value, fallback) || fallback;
  latestHexRef.current = hex;
  const [hsv, setHsv] = React.useState<SlideHsv>(() => hexToSlideHsv(hex) ?? { h: 0, s: 0, v: 0 });
  hsvRef.current = hsv;

  const suggestions = React.useMemo(
    () => mergeSlideColorSuggestions(recentColors, usedColors),
    [recentColors, usedColors]
  );
  const rgb = parseSlideRgbHex(hex) ?? { r: 0, g: 0, b: 0 };
  const canEyedrop = Boolean(getEyeDropperCtor());

  const closeAndConfirm = React.useCallback(() => {
    setOpen(false);
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    onConfirm(latestHexRef.current);
  }, [onConfirm]);

  const previewFromHsv = React.useCallback(
    (nextHsv: SlideHsv) => {
      const nextHex = hsvToSlideHex(nextHsv);
      hsvRef.current = nextHsv;
      setHsv(nextHsv);
      latestHexRef.current = nextHex;
      dirtyRef.current = true;
      onPreview(nextHex);
    },
    [onPreview]
  );

  const applyConfirmedHex = React.useCallback(
    (nextRaw: string) => {
      const next = normalizeSlidePickerHex(nextRaw, latestHexRef.current) || latestHexRef.current;
      latestHexRef.current = next;
      dirtyRef.current = false;
      const nextHsv = hexToSlideHsv(next);
      if (nextHsv) {
        hsvRef.current = nextHsv;
        setHsv(nextHsv);
      }
      onConfirm(next);
      setOpen(false);
    },
    [onConfirm]
  );

  const updatePanelPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger) return;
    const { height: vh } = getLayoutViewportSize();
    const maxH = Math.max(160, vh - 16);
    const measuredH = Math.min(panel?.offsetHeight || (suggestions.length ? 268 : 228), maxH);
    const measuredW = panel?.offsetWidth || 232;
    const next = computeZoomAwareAnchorCenteredPopupLayout({
      anchorRect: trigger.getBoundingClientRect(),
      popoverWidth: measuredW,
      popoverHeight: measuredH,
      gap: 8,
      pad: 8,
    });
    setPanelStyle({
      position: 'fixed',
      top: next.top,
      left: next.left,
      zIndex: 50000,
      maxHeight: maxH,
      overflowY: 'auto',
    });
  }, [suggestions.length]);

  React.useLayoutEffect(() => {
    if (!open) {
      setPanelStyle(null);
      return;
    }
    const nextHsv = hexToSlideHsv(latestHexRef.current);
    if (nextHsv) {
      hsvRef.current = nextHsv;
      setHsv(nextHsv);
    }
    updatePanelPosition();
    const raf = window.requestAnimationFrame(() => updatePanelPosition());
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePanelPosition);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, updatePanelPosition);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;
    const nextHsv = hexToSlideHsv(hex);
    if (!nextHsv) return;
    if (hsvToSlideHex(hsvRef.current) === hex) return;
    hsvRef.current = nextHsv;
    setHsv(nextHsv);
  }, [hex, open]);

  React.useEffect(() => {
    if (!open) return;
    let armed = false;
    const arm = window.setTimeout(() => {
      armed = true;
    }, 0);
    const onDocPointerDown = (e: PointerEvent) => {
      if (!armed) return;
      const t = e.target as Node | null;
      if (triggerRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      closeAndConfirm();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeAndConfirm();
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.clearTimeout(arm);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [closeAndConfirm, open]);

  const bindRatioDrag = (el: HTMLElement, apply: (ratio: { x: number; y: number }) => void) => {
    const onMove = (ev: PointerEvent) => apply(pointerRatio(el, ev.clientX, ev.clientY));
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  };

  const onSvPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const apply = (ratio: { x: number; y: number }) => {
      previewFromHsv({ ...hsvRef.current, s: ratio.x, v: 1 - ratio.y });
    };
    apply(pointerRatio(el, e.clientX, e.clientY));
    bindRatioDrag(el, apply);
  };

  const onHuePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const el = e.currentTarget;
    const apply = (ratio: { x: number }) => {
      previewFromHsv({ ...hsvRef.current, h: ratio.x * 360 });
    };
    apply(pointerRatio(el, e.clientX, e.clientY));
    bindRatioDrag(el, apply);
  };

  const onRgbChannelChange = (key: 'r' | 'g' | 'b', raw: string) => {
    const next = { ...rgb, [key]: clampByte(Number.parseInt(raw, 10)) };
    const nextHex = `#${[next.r, next.g, next.b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
    const nextHsv = hexToSlideHsv(nextHex);
    if (!nextHsv) return;
    previewFromHsv(nextHsv);
  };

  const onHexDraftChange = (raw: string) => {
    const next = normalizeSlidePickerHex(raw.startsWith('#') ? raw : `#${raw}`, '');
    if (!next) return;
    const nextHsv = hexToSlideHsv(next);
    if (!nextHsv) return;
    previewFromHsv(nextHsv);
  };

  const onEyedrop = async () => {
    const Ctor = getEyeDropperCtor();
    if (!Ctor) return;
    try {
      const result = await new Ctor().open();
      const next = normalizeSlidePickerHex(result.sRGBHex, '');
      if (!next) return;
      const nextHsv = hexToSlideHsv(next);
      if (!nextHsv) return;
      previewFromHsv(nextHsv);
    } catch {
      /* 사용자가 스포이드를 취소한 경우 */
    }
  };

  return (
    <span className={'cp-program-slide-canvas-color-wrap' + (open ? ' is-picker-open' : '')}>
      <span
        className="cp-program-slide-canvas-color-swatch"
        style={{ backgroundColor: hex, ...swatchStyle }}
        aria-hidden
      />
      <button
        ref={triggerRef}
        type="button"
        className="cp-slide-color-picker-trigger"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onPointerDown={(e) => {
          e.stopPropagation();
          onOpen?.();
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          if (open) {
            closeAndConfirm();
            return;
          }
          setOpen(true);
        }}
      />
      {open && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              className={
                'cp-slide-color-picker-modal' +
                (surface === 'light' ? ' cp-slide-color-picker-modal--light' : '')
              }
              role="dialog"
              aria-label={ariaLabel}
              style={panelStyle ?? { position: 'fixed', visibility: 'hidden', zIndex: 50000 }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div
                className="cp-slide-color-picker-sv"
                style={{
                  backgroundImage: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueToCssHsl(hsv.h)})`,
                }}
                onPointerDown={onSvPointerDown}
              >
                <span
                  className="cp-slide-color-picker-sv-thumb"
                  style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
                  aria-hidden
                />
              </div>
              <div className="cp-slide-color-picker-controls">
                {canEyedrop ? (
                  <button
                    type="button"
                    className="cp-slide-color-picker-eye"
                    aria-label="화면에서 색 추출"
                    onClick={() => void onEyedrop()}
                  >
                    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden>
                      <path
                        fill="currentColor"
                        d="M6.2 13.1 3 16.3 3.7 17l3.2-3.2.8.8 1.1-1.1-4.3-4.3-1.1 1.1.8.8Zm8.2-8.6c.6.6.6 1.5 0 2.1l-.8.8-2.9-2.9.8-.8c.6-.6 1.5-.6 2.1 0l.8.8ZM5.4 9.7l4.9 4.9 5.3-5.3c.7-.7 1.1-1.6 1.1-2.6 0-.4-.1-.8-.3-1.2l-2.2 2.2-2.9-2.9 2.2-2.2c-.4-.2-.8-.3-1.2-.3-1 0-1.9.4-2.6 1.1L5.4 9.7Z"
                      />
                    </svg>
                  </button>
                ) : null}
                <span className="cp-slide-color-picker-current" style={{ backgroundColor: hex }} aria-hidden />
                <div className="cp-slide-color-picker-hue" onPointerDown={onHuePointerDown} aria-label="색상">
                  <span
                    className="cp-slide-color-picker-hue-thumb"
                    style={{ left: `${(hsv.h / 360) * 100}%` }}
                    aria-hidden
                  />
                </div>
              </div>
              <div className="cp-slide-color-picker-channels">
                {format === 'rgb' ? (
                  <>
                    <label className="cp-slide-color-picker-channel">
                      <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.r}
                        aria-label="R"
                        onChange={(e) => onRgbChannelChange('r', e.currentTarget.value)}
                      />
                      <span>R</span>
                    </label>
                    <label className="cp-slide-color-picker-channel">
                      <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.g}
                        aria-label="G"
                        onChange={(e) => onRgbChannelChange('g', e.currentTarget.value)}
                      />
                      <span>G</span>
                    </label>
                    <label className="cp-slide-color-picker-channel">
                      <input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb.b}
                        aria-label="B"
                        onChange={(e) => onRgbChannelChange('b', e.currentTarget.value)}
                      />
                      <span>B</span>
                    </label>
                  </>
                ) : (
                  <label className="cp-slide-color-picker-channel cp-slide-color-picker-channel--hex">
                    <input
                      type="text"
                      spellCheck={false}
                      value={hex.replace('#', '').toUpperCase()}
                      aria-label="HEX"
                      onChange={(e) => onHexDraftChange(e.currentTarget.value)}
                    />
                    <span>HEX</span>
                  </label>
                )}
                <button
                  type="button"
                  className="cp-slide-color-picker-format"
                  aria-label={format === 'rgb' ? 'HEX로 전환' : 'RGB로 전환'}
                  onClick={() => setFormat((cur) => (cur === 'rgb' ? 'hex' : 'rgb'))}
                >
                  <svg viewBox="0 0 12 16" width="10" height="14" aria-hidden>
                    <path fill="currentColor" d="M6 1 2.5 5.5h7L6 1Zm0 14 3.5-4.5h-7L6 15Z" />
                  </svg>
                </button>
              </div>
              {suggestions.length ? (
                <div className="cp-slide-color-picker-swatches" aria-label="캔버스에 쓰인 색">
                  {suggestions.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={'cp-slide-color-picker-chip' + (c === hex ? ' is-current' : '')}
                      style={{ backgroundColor: c }}
                      title={c}
                      aria-label={`색상 ${c}`}
                      onClick={() => applyConfirmedHex(c)}
                    />
                  ))}
                </div>
              ) : null}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}
